import { Colors } from '@oy/uicore';
import { SystemIcon } from '@oy/uiicon';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import Panel from './Panel';
import Tab, { TabProps, TabType } from './Tab';
import styles from './Tabs.module.css';
import useTabs from './useTabs';

export type TabsProps = {
  activeIndex: number;
  align?: 'stretch' | 'start';
  children: React.ReactElement<TabProps>[];
  activeType?: TabType;
  isJustifyContentCenter?: boolean;
  isUsingBorderBottom?: boolean;
  activeArrow?: boolean;
  onChange: (index: number) => void;
};

const Tabs = ({
  activeIndex,
  children,
  align = 'stretch',
  activeType = 'bottomLine',
  isJustifyContentCenter = false,
  isUsingBorderBottom = true,
  activeArrow = false,
  onChange,
}: TabsProps) => {
  const container = classNames(styles.container, {
    [styles.rounded]: activeType == 'rounded' || activeArrow,
  });

  const classes = classNames(styles.wrapper, {
    [styles.stretch]: align == 'stretch',
  });

  let scroll = useRef<HTMLDivElement>(null);
  const [scrollX, setScrollX] = useState(0);
  const [scrollEnd, setScrollEnd] = useState(true);

  //Slide click
  const slide = (shift: number) => {
    if (scroll?.current) {
      scroll.current.scrollLeft += shift;
      setScrollX(scrollX + shift);

      if (
        Math.floor(scroll.current.scrollWidth - scroll.current.scrollLeft) <=
        scroll.current.offsetWidth
      ) {
        setScrollEnd(true);
      } else {
        setScrollEnd(false);
      }
    }
  };

  const scrollCheck = () => {
    if (scroll?.current) {
      setScrollX(scroll.current.scrollLeft);
      if (
        Math.floor(scroll.current.scrollWidth - scroll.current.scrollLeft) <=
        scroll.current.offsetWidth
      ) {
        setScrollEnd(true);
      } else {
        setScrollEnd(false);
      }
    }
  };

  useEffect(() => {
    scrollCheck();
  }, []);

  return (
    <div
      className={classNames(styles.tabWrapper, {
        [styles.justifyContentCenter]: isJustifyContentCenter,
        [styles.borderBottom]: isUsingBorderBottom,
      })}
    >
      {(activeType == 'rounded' || activeArrow) && scrollX !== 0 && (
        <div className={styles.left} onClick={() => slide(-50)}>
          <SystemIcon
            color={Colors.BLACK100}
            size={20}
            name="arrow-left-outline"
          />
        </div>
      )}
      <div className={container} ref={scroll} onScroll={scrollCheck}>
        {React.Children.map(children, (child, i) => (
          <div className={classes}>
            {React.cloneElement(child, {
              active: activeIndex === i,
              activeType: activeType,
              onClick: () => {
                child.props.onClick?.();
                onChange(i);
              },
            })}
            {activeType == 'bottomLine' && (
              <div
                className={
                  activeIndex === i
                    ? styles.bottomBorderActive
                    : styles.bottomBorderInactive
                }
              />
            )}
          </div>
        ))}
      </div>
      {(activeType == 'rounded' || activeArrow) && !scrollEnd && (
        <div className={styles.right} onClick={() => slide(50)}>
          <SystemIcon
            color={Colors.BLACK100}
            size={20}
            name="arrow-right-outline"
          />
        </div>
      )}
    </div>
  );
};

Tabs.Tab = Tab;
Tabs.Panel = Panel;
Tabs.useTabs = useTabs;

export default Tabs;
